import { Button, Link } from '@mui/material';
import jwt from 'jsonwebtoken';
import { useEffect, useState } from 'react';
import {
  type EscapeHatchUserContext,
  getSignedInEmail,
  type LoadFailure,
  type LoadFailureView,
  resolveLoadFailureView,
  type SwitchableCompany,
} from '@/frontend/src/app/common/loading/loadFailureView';

/**
 * How long the spinner can run before we assume the bootstrap is stuck
 * (e.g. a never-resolving auth renewal) and surface the sign-out escape
 * hatch even though no error was thrown.
 */
const ESCAPE_HATCH_DELAY_MS = 15_000;

interface LoadingPageProps {
  /**
   * Enables the sign-out escape hatch. When provided, a "Sign Out" button is
   * shown once `loadError` is set or the spinner has been up longer than
   * expected, so a broken bootstrap (login errors, hung requests) never
   * strands the user on an infinite spinner with no way out.
   */
  onSignOut?: () => void;
  /**
   * Details of the error that broke the bootstrap sequence — shows the
   * escape hatch immediately. The status/code decide which failure view is
   * rendered (see resolveLoadFailureView).
   */
  loadError?: LoadFailure;
  userContext?: EscapeHatchUserContext;
  auth?: { getToken: () => unknown };
  /**
   * Lets the user recover by switching to a company they still have access
   * to (same flow as the sidebar switcher: PUT /api/user + full reload).
   * Only needs authentication, not company permissions, so it works even
   * when the bootstrap failed.
   */
  onSwitchCompany?: (companyId: string) => Promise<void>;
}

function LoadingPage({
  onSignOut,
  loadError,
  userContext,
  auth,
  onSwitchCompany,
}: LoadingPageProps) {
  const [timedOut, setTimedOut] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [switchingCompanyId, setSwitchingCompanyId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const timeout = setTimeout(() => setTimedOut(true), ESCAPE_HATCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, []);

  const showEscapeHatch =
    Boolean(onSignOut) && (Boolean(loadError) || timedOut);

  const failureView = loadError
    ? resolveLoadFailureView(userContext, loadError)
    : null;
  const signedInEmail = getSignedInEmail(userContext, auth);

  const switchCompany = (companyId: string) => {
    if (!onSwitchCompany || switchingCompanyId) {
      return;
    }
    setSwitchingCompanyId(companyId);
    onSwitchCompany(companyId).catch(() => {
      setSwitchingCompanyId(null);
    });
  };

  const renderCompanyList = (label: string, companies: SwitchableCompany[]) => {
    if (companies.length === 0 || !onSwitchCompany) {
      return null;
    }
    return (
      <div className="loader-escape-company-list">
        <p className="loader-escape-list-label">{label}</p>
        {companies.map((company) => (
          <div className="loader-escape-company-row" key={company.companyId}>
            <span className="loader-escape-company-name">
              {company.companyName}
            </span>
            {company.roleLabel && (
              <span className="loader-escape-role-chip">
                {company.roleLabel}
              </span>
            )}
            <Button
              variant="contained"
              size="small"
              disabled={Boolean(switchingCompanyId)}
              sx={{
                backgroundColor: '#0d0c15',
                color: '#fff',
                '&:hover': { backgroundColor: '#6d60b5', color: '#fff' },
              }}
              onClick={() => switchCompany(company.companyId)}
            >
              {switchingCompanyId === company.companyId
                ? 'Switching…'
                : 'Switch'}
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderFailureContent = (view: LoadFailureView) => {
    switch (view.kind) {
      case 'no-companies':
        return (
          <>
            <p className="loader-escape-heading">
              Your account isn’t part of any company yet
            </p>
            <p>
              You haven’t been added to a company, or your access was removed.
              Ask the admin at your organization to invite
              {signedInEmail ? (
                <>
                  {' '}
                  <b>{signedInEmail}</b>
                </>
              ) : (
                ' you'
              )}{' '}
              to their AI Insurance instance.
            </p>
          </>
        );
      case 'lost-company-access':
        return (
          <>
            <p className="loader-escape-heading">
              {view.companyName
                ? `You no longer have access to ${view.companyName}`
                : 'You no longer have access to your current company'}
            </p>
            <p>
              Your account was last signed in to this company, but you don’t
              have a role there anymore.
              {view.companies.length > 0 &&
                ' Switch to one of your companies to continue.'}
            </p>
            {renderCompanyList('Your companies', view.companies)}
          </>
        );
      case 'insufficient-permissions':
        return (
          <>
            <p className="loader-escape-heading">
              {view.permissionDenied
                ? 'It looks like your current role has insufficient permissions to load the dashboard'
                : 'Something went wrong loading the dashboard'}
            </p>
            {view.companyName && (
              <p>
                You’re signed in to <b>{view.companyName}</b>
                {view.roleLabel && (
                  <>
                    {' '}
                    as{' '}
                    <span className="loader-escape-role-chip">
                      {view.roleLabel}
                    </span>
                  </>
                )}
                .
              </p>
            )}
            <p>
              {view.permissionDenied
                ? 'Ask an administrator at your company to review your role.'
                : 'Refreshing may fix the problem. If it keeps happening, ask an administrator at your company to check your role, or contact support.'}
            </p>
            {renderCompanyList('Your other companies', view.otherCompanies)}
          </>
        );
      case 'generic':
        return (
          <>
            <p>
              Something went wrong while loading. Signing out and back in may
              fix the problem.
            </p>
            {signedInEmail && (
              <p>
                If that doesn’t help, contact support and mention the address
                you signed in with: <b>{signedInEmail}</b>.
              </p>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {!showEscapeHatch && (
        <>
          <div id="particles-background" className="vertical-centered-box" />
          <div id="particles-foreground" className="vertical-centered-box" />

          <div className="vertical-centered-box">
            <div className="loader-content">
              <div className="loader-circle" />
              <div className="loader-line-mask">
                <div className="loader-line" />
              </div>
              <svg width="36px" height="24px" viewBox="0 0 36 24" version="1.1">
                <path
                  d="M8.98885921,23.8523026 C8.8942483,23.9435442 8.76801031,24 8.62933774,24 L2.03198365,24 C1.73814918,24 1.5,23.7482301 1.5,23.4380086 C1.5,23.2831829 1.55946972,23.1428989 1.65570253,23.0416777 L13.2166154,12.4291351 C13.3325814,12.3262031 13.4061076,12.1719477 13.4061076,11.999444 C13.4061076,11.8363496 13.3401502,11.6897927 13.2352673,11.587431 L1.68841087,0.990000249 C1.57298556,0.88706828 1.5,0.733668282 1.5,0.561734827 C1.5,0.251798399 1.73814918,2.85130108e-05 2.03198365,2.85130108e-05 L8.62933774,2.85130108e-05 C8.76855094,2.85130108e-05 8.89532956,0.0561991444 8.98994048,0.148296169 L21.4358709,11.5757407 C21.548593,11.6783875 21.6196864,11.8297916 21.6196864,11.999444 C21.6196864,12.1693815 21.5483227,12.3219261 21.4350599,12.4251432 L8.98885921,23.8523026 Z M26.5774333,23.8384453 L20.1765996,17.9616286 C20.060093,17.8578413 19.9865669,17.703871 19.9865669,17.5310822 C19.9865669,17.3859509 20.0390083,17.2536506 20.1246988,17.153855 L23.4190508,14.1291948 C23.5163648,14.0165684 23.6569296,13.945571 23.8131728,13.945571 C23.9602252,13.945571 24.0929508,14.0082997 24.1894539,14.1092357 L33.861933,22.9913237 C33.9892522,23.0939706 34.0714286,23.2559245 34.0714286,23.4381226 C34.0714286,23.748059 33.8332794,23.9998289 33.5394449,23.9998289 L26.9504707,23.9998289 C26.8053105,23.9998289 26.6733958,23.9382408 26.5774333,23.8384453 Z M26.5774333,0.161098511 C26.6733958,0.0615881034 26.8053105,0 26.9504707,0 L33.5394449,0 C33.8332794,0 34.0714286,0.251769886 34.0714286,0.561706314 C34.0714286,0.743904453 33.9892522,0.905573224 33.861933,1.00822006 L24.1894539,9.89030807 C24.0929508,9.99152926 23.9602252,10.0542579 23.8131728,10.0542579 C23.6569296,10.0542579 23.5163648,9.98354562 23.4190508,9.87063409 L20.1246988,6.8459739 C20.0390083,6.74617837 19.9865669,6.613878 19.9865669,6.46874677 C19.9865669,6.29624305 20.060093,6.14198767 20.1765996,6.03848544 L26.5774333,0.161098511 Z"
                  fill="#FFFFFF"
                />
              </svg>
            </div>
          </div>
        </>
      )}

      {showEscapeHatch && (
        <div className="loader-escape-hatch">
          {failureView ? (
            renderFailureContent(failureView)
          ) : (
            <p>
              This is taking longer than expected. If you're stuck, try signing
              out and back in.
            </p>
          )}
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#0d0c15',
              color: '#fff',
              '&:hover': { backgroundColor: '#6d60b5', color: '#fff' },
            }}
            onClick={onSignOut}
          >
            Sign Out
          </Button>

          <Link
            component="button"
            type="button"
            sx={{
              display: 'block',
              margin: '16px auto 0',
              fontSize: 12,
              color: '#0d0c15',
              '&:hover': { color: '#6d60b5' },
            }}
            onClick={() => setShowErrorDetails(!showErrorDetails)}
          >
            {showErrorDetails
              ? 'Hide technical details'
              : 'Show technical details'}
          </Link>
          {showErrorDetails && (
            <>
              <p className="loader-escape-error">{loadError?.message}</p>
              {(loadError?.status || loadError?.code) && (
                <p className="loader-escape-error">
                  {[
                    loadError?.status && `status: ${loadError.status}`,
                    loadError?.code && `code: ${loadError.code}`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
              <p className="loader-escape-error">
                {(() => {
                  try {
                    return userContext?.id ?? 'No userId yet';
                  } catch (error) {
                    return `Unable to get userId: ${error instanceof Error ? error.message : String(error)}`;
                  }
                })()}
              </p>
              <p className="loader-escape-error">
                {(() => {
                  try {
                    return userContext?.companyId ?? 'No companyId yet';
                  } catch (error) {
                    return `Unable to get companyId: ${error instanceof Error ? error.message : String(error)}`;
                  }
                })()}
              </p>
              <p className="loader-escape-error">
                {(() => {
                  try {
                    const token = auth?.getToken();

                    if (!token) {
                      return 'No token yet';
                    }
                    if (typeof token !== 'string') {
                      return 'Token is not a string';
                    }

                    const decoded = jwt.decode(token);
                    if (!decoded) {
                      return 'No decodable token';
                    }
                    if (typeof decoded === 'string') {
                      return decoded;
                    }

                    return <pre>{JSON.stringify(decoded, null, 2)}</pre>;
                  } catch (error) {
                    return `Unable to get token: ${error instanceof Error ? error.message : String(error)}`;
                  }
                })()}
              </p>
            </>
          )}
        </div>
      )}
    </>
  );
}

export default LoadingPage;
